import React, { useState, useEffect, useRef } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import AdminNavbar from '../components/AdminNavbar';
import { useSidebar } from '../components/SidebarContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const AddNewSection = () => {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  // State declarations
  const [sections, setSections] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [sectionModalNotification, setSectionModalNotification] = useState(null);
  const [departmentModalNotification, setDepartmentModalNotification] = useState(null);
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [isAddDepartmentModalOpen, setIsAddDepartmentModalOpen] = useState(false);
  const [isEditMappingModalOpen, setIsEditMappingModalOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [newSection, setNewSection] = useState('');
  const [newDepartment, setNewDepartment] = useState('');
  const [loading, setLoading] = useState(false);
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [sectionSearch, setSectionSearch] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isAddingDepartment, setIsAddingDepartment] = useState(false);

  const sectionDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // useEffect hooks
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sectionDropdownRef.current && !sectionDropdownRef.current.contains(event.target) && isSectionDropdownOpen) {
        setIsSectionDropdownOpen(false);
        setSectionSearch('');
      }
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target) && isDepartmentDropdownOpen) {
        setIsDepartmentDropdownOpen(false);
        setDepartmentSearch('');
        setIsAddingDepartment(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSectionDropdownOpen, isDepartmentDropdownOpen]);

  useEffect(() => {
    if (notifications.length > 0) {
      const timers = notifications.map((notification, index) =>
        setTimeout(() => {
          setNotifications((prev) => prev.filter((_, i) => i !== index));
        }, 5000)
      );
      return () => timers.forEach((timer) => clearTimeout(timer));
    }
  }, [notifications]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSections(),
        fetchDepartments(),
        fetchMappings()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/sections`);
      if (!response.ok) throw new Error('Failed to fetch sections');
      const text = await response.text();
      const data = JSON.parse(text);
      setSections(data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      showNotification('Error fetching sections', 'error');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/departments`);
      if (!response.ok) throw new Error('Failed to fetch departments');
      const text = await response.text();
      const data = JSON.parse(text);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      showNotification('Error fetching departments', 'error');
    }
  };

  const fetchMappings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/mappings`);
      if (!response.ok) throw new Error('Failed to fetch mappings');
      const text = await response.text();
      const data = JSON.parse(text);
      const validMappings = data.filter(
        mapping => mapping.section && mapping.department && mapping.section._id && mapping.department._id
      );
      console.log('Fetched mappings:', validMappings);
      setMappings(validMappings);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      showNotification('Error fetching mappings', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    const newNotification = { message, type, id: Date.now() };
    setNotifications((prev) => [...prev, newNotification]);
  };

  const showSectionModalNotification = (message, type = 'success') => {
    const newNotification = { message, type, id: Date.now() };
    setSectionModalNotification(newNotification);
    setTimeout(() => {
      setSectionModalNotification(null);
    }, 5000);
  };

  const showDepartmentModalNotification = (message, type = 'success') => {
    const newNotification = { message, type, id: Date.now() };
    setDepartmentModalNotification(newNotification);
    setTimeout(() => {
      setDepartmentModalNotification(null);
    }, 5000);
  };

  const handleAddSection = async () => {
    if (!newSection.trim()) {
      showSectionModalNotification('Section name cannot be empty', 'error');
      return;
    }
    const existingSection = sections.find(
      section => section.name.toLowerCase() === newSection.toLowerCase()
    );
    if (existingSection) {
      showSectionModalNotification('Section already exists', 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newSection }),
      });
      if (!response.ok) {
        const text = await response.text();
        const error = JSON.parse(text);
        throw new Error(error.error || 'Failed to create section');
      }
      const newSectionData = await response.json();
      setSections([...sections, newSectionData]);
      setNewSection('');
      setIsAddSectionModalOpen(false);
      showNotification('Successfully added section', 'success');
    } catch (error) {
      console.error('Error adding section:', error);
      showNotification('Failed to add section', 'error');
    } finally {
      setLoading(false);
    }
  };

const handleAddDepartment = async () => {
  if (!newDepartment.trim()) {
    showDepartmentModalNotification('Department name cannot be empty', 'error');
    return;
  }
  const existingDepartment = departments.find(
    dept => dept.name.toLowerCase() === newDepartment.toLowerCase()
  );
  if (existingDepartment) {
    showDepartmentModalNotification('Department already exists', 'error');
    return;
  }
  setLoading(true);
  try {
    const response = await fetch(`${API_BASE_URL}/departments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: newDepartment }),
    });
    if (!response.ok) {
      const text = await response.text();
      const error = JSON.parse(text);
      throw new Error(error.error || 'Failed to create department');
    }
    const newDepartmentData = await response.json();
    setDepartments([...departments, newDepartmentData]);
    setNewDepartment('');
    setIsAddDepartmentModalOpen(false);
    setIsAddingDepartment(false);
    setIsDepartmentDropdownOpen(false);
    showNotification('Successfully added department', 'success');
  } catch (error) {
    console.error('Error adding department:', error);
    showNotification('Failed to add department', 'error');
  } finally {
    setLoading(false);
  }
};
  const handleEditMapping = (mapping) => {
    if (!mapping.department) {
      showNotification('No department associated with mapping', 'error');
      return;
    }
    const departmentId = mapping.department._id;
    const departmentMappings = mappings.filter(m => m.department && m.department._id === departmentId);
    const sectionIds = departmentMappings.map(m => m.section._id);
    const selectedSectionsForDepartment = sections.filter(s => sectionIds.includes(s._id));
    setSelectedMapping(mapping);
    setSelectedDepartment(departments.find(d => d._id === departmentId) || null);
    setSelectedSections(selectedSectionsForDepartment);
    setIsEditMappingModalOpen(true);
  };

  const handleSaveMapping = async () => {
    if (!selectedDepartment || selectedSections.length === 0) {
      showNotification('Please select a department and at least one section', 'error');
      return;
    }
    setLoading(true);
    try {
      const existingMappings = mappings.filter(m => m.department && m.department._id === selectedDepartment._id);
      const deletePromises = existingMappings.map(m =>
        fetch(`${API_BASE_URL}/mappings/${m._id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        })
      );
      const responses = await Promise.allSettled(deletePromises);
      const failedDeletions = responses.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok)).map(r => {
        if (r.status === 'rejected') return r.reason;
        return { status: r.value.status, text: r.value.statusText };
      });
      if (failedDeletions.length > 0) {
        console.error('Failed to delete some mappings:', failedDeletions);
        throw new Error('Some mappings could not be deleted');
      }

      let successCount = 0;
      let failedMappings = [];
      for (const section of selectedSections) {
        try {
          const response = await fetch(`${API_BASE_URL}/mappings/map`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sectionId: section._id,
              departmentId: selectedDepartment._id,
            }),
          });
          if (!response.ok) {
            const text = await response.text();
            try {
              const errorData = text ? JSON.parse(text) : { error: `HTTP ${response.status}` };
              throw new Error(errorData.error || `Failed to create mapping: HTTP ${response.status}`);
            } catch (parseError) {
              throw new Error(`Non-JSON response received: ${text.substring(0, 50)}...`);
            }
          }
          successCount++;
        } catch (error) {
          failedMappings.push({ section: section.name, error: error.message });
        }
      }

      await fetchAllData();
      if (failedMappings.length > 0) {
        showNotification('Failed to map some sections', 'error');
      }
      if (successCount > 0) {
        showNotification('Successfully mapped the section', 'success');
      }
      setIsEditMappingModalOpen(false);
      setSelectedMapping(null);
      setSelectedDepartment(null);
      setSelectedSections([]);
    } catch (error) {
      console.error('Error updating mappings:', error);
      showNotification('Failed to update mappings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    const departmentName = departments.find(d => d._id === departmentId)?.name || 'this department';
    if (window.confirm(`Are you sure you want to delete all mappings and the department itself for ${departmentName}?`)) {
      setLoading(true);
      try {
        const departmentMappings = mappings.filter(m => m.department && m.department._id === departmentId);
        if (!departmentMappings.length) {
          console.log(`No mappings found for department ID: ${departmentId}`);
        }

        for (const mapping of departmentMappings) {
          const response = await fetch(`${API_BASE_URL}/mappings/${mapping._id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!response.ok) {
            const text = await response.text();
            console.log(`Failed to delete mapping ${mapping._id}: ${text}`);
            continue;
          }
        }

        const departmentResponse = await fetch(`${API_BASE_URL}/departments/${departmentId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!departmentResponse.ok) {
          const text = await departmentResponse.text();
          try {
            const errorData = text ? JSON.parse(text) : { error: `HTTP ${departmentResponse.status}` };
            throw new Error(errorData.error || `Failed to delete department: HTTP ${departmentResponse.status}`);
          } catch (parseError) {
            throw new Error(`Non-JSON response received: ${text.substring(0, 50)}...`);
          }
        }

        showNotification('Successfully deleted the department', 'success');
        await fetchAllData();
      } catch (error) {
        console.error('Error deleting department and its mappings:', error);
        showNotification('Mapping deleted successfully', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const isSectionDepartmentMapped = (sectionId, departmentId) => {
    return mappings.some(mapping => 
      mapping.section && mapping.department &&
      mapping.section._id === sectionId && 
      mapping.department._id === departmentId
    );
  };

  const handleSectionSelect = (section) => {
    if (selectedSections.some(s => s._id === section._id)) {
      setSelectedSections(selectedSections.filter((s) => s._id !== section._id));
    } else if (selectedSections.length < 3) {
      setSelectedSections([...selectedSections, section]);
    } else {
      showNotification('Maximum 3 sections can be selected', 'error');
    }
    setIsSectionDropdownOpen(false);
    setSectionSearch('');
  };

  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
    setDepartmentSearch(department.name);
    setIsDepartmentDropdownOpen(false);
    setIsAddingDepartment(false);
  };

  const handleMap = async () => {
    if (!selectedDepartment || selectedSections.length === 0) {
      showNotification('Please select a department and at least one section', 'error');
      return;
    }
    setLoading(true);
    try {
      let successCount = 0;
      let failedMappings = [];
      let alreadyMappedCombinations = [];
      for (const section of selectedSections) {
        if (isSectionDepartmentMapped(section._id, selectedDepartment._id)) {
          alreadyMappedCombinations.push(`${section.name} - ${selectedDepartment.name}`);
          continue;
        }
        try {
          const response = await fetch(`${API_BASE_URL}/mappings/map`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sectionId: section._id,
              departmentId: selectedDepartment._id,
            }),
          });
          if (response.ok) {
            successCount++;
          } else {
            const responseText = await response.text();
            let errorMessage = 'Unknown error';
            try {
              const errorData = responseText ? JSON.parse(responseText) : {};
              errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
            } catch (parseError) {
              errorMessage = `HTTP ${response.status}: ${responseText || 'Unknown error'}`;
            }
            failedMappings.push({
              section: section.name,
              department: selectedDepartment.name,
              error: errorMessage
            });
          }
        } catch (networkError) {
          failedMappings.push({
            section: section.name,
            department: selectedDepartment.name,
            error: 'Network error: ' + networkError.message
          });
        }
      }
      await fetchAllData();
      if (alreadyMappedCombinations.length > 0) {
        showNotification(`${alreadyMappedCombinations.join(', ')} already mapped`, 'warning');
      }
      if (failedMappings.length > 0) {
        showNotification('Failed to map some sections', 'error');
      }
      if (successCount > 0) {
        showNotification('Successfully mapped the section', 'success');
      }
      setSelectedSections([]);
      setSelectedDepartment(null);
      setDepartmentSearch('');
    } catch (error) {
      console.error('Error in handleMap:', error);
      showNotification('Failed to map sections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = (id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const handleCloseSectionModalNotification = () => {
    setSectionModalNotification(null);
  };

  const handleCloseDepartmentModalNotification = () => {
    setDepartmentModalNotification(null);
  };

  const handleDepartmentInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen);
    }
  };

  const handleDepartmentIconKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen);
    }
  };

  const groupedMappings = departments.reduce((acc, department) => {
    const departmentMappings = mappings.filter(
      mapping => mapping.department && mapping.department._id === department._id
    );
    if (departmentMappings.length > 0) {
      const validMappings = departmentMappings.filter(
        mapping => mapping.section && mapping.department
      );
      if (validMappings.length > 0) {
        acc.push({ 
          department: department.name, 
          sections: validMappings.map(mapping => mapping.section.name),
          mappings: validMappings
        });
      }
    }
    return acc;
  }, []);

  const filteredDepartments = departments.filter((department) =>
    department.name.toLowerCase().includes(departmentSearch.toLowerCase())
  );

  const filteredSections = sections.filter((section) =>
    section.name.toLowerCase().includes(sectionSearch.toLowerCase())
  );

  const layoutStyles = {
    container: {
      display: 'flex',
      height: '100vh',
      minHeight: '90vh',
      background: 'linear-gradient(to right, #F0F0F0, #F3E8FF, #FEE7EF)',
      fontFamily: "'Roboto', sans-serif",
      padding: isMobile ? '0.5rem' : '1rem',
      overflowX: 'hidden',
      overflowY: 'hidden',
      boxSizing: 'border-box',
    },
    sidebar: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100%',
      width: sidebarWidth,
      zIndex: 10,
      transition: 'width 0.3s ease',
      borderRadius: '30px',
      flexShrink: 0,
      background: 'transparent',
      fontFamily: "'Roboto', sans-serif",
    },
    mainContent: {
      marginLeft: sidebarWidth,
      flex: 1,
      transition: 'margin-left 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px 30px',
      margin: '5rem 3rem 1rem 1rem',
      backgroundColor: 'white',
      overflow: 'hidden',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      maxHeight: 'calc(100vh - 8rem - 50px)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationContainer: {
      position: 'absolute',
      top: '15px',
      right: '30px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    modalNotificationContainer: {
      marginBottom: '0.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    sectionExistsNotificationContainer: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      width: '200px',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    notification: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      fontWeight: '400',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      maxWidth: '300px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 0.5s ease-out',
    },
    modalNotification: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      fontWeight: '400',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 0.5s ease-out',
    },
    notificationSuccess: {
      backgroundColor: '#F3E8FF',
      color: '#6B21A8',
    },
    notificationError: {
      backgroundColor: '#F3E8FF',
      color: '#6B21A8',
    },
    notificationClose: {
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    sectionBox: {
      marginTop: '0.5rem',
      position: 'relative',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    buttonsContainer: {
      display: "flex",
      gap: "1rem",
      justifyContent: "flex-start",
      marginTop: "0.75rem",
      fontFamily: "'Roboto', sans-serif",
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem 1.2rem',
      fontFamily: "'Roboto', sans-serif",
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '0.05rem',
      cursor: 'pointer',
      borderRadius: '1.5rem',
      border: '2px solid transparent',
      backgroundImage:
        'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
    },
    mapButton: {
      fontWeight: 'normal',
      padding: '0.3rem 0.8rem',
      fontWeight: '600',
      fontSize: '0.75rem',
      marginTop: '0.5rem',
      borderRadius: '1rem',
      fontFamily: "'Roboto', sans-serif",
    },
    mapSection: {
      position: 'relative',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    mapRow: {
      display: 'flex',
      gap: '1.8rem',
      justifyContent: 'flex-start',
      marginTop: '0.75rem',
      alignItems: 'center',
      fontFamily: "'Roboto', sans-serif",
      position: 'relative',
      minHeight: '20px',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
    },
    listContainer: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '1.5rem',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      justifyContent: 'space-between',
      width: '100%',
      overflowX: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    tableContainer: {
      flex: 1,
      padding: '0.75rem',
      border: '1px solid #ddd',
      borderRadius: '8px',
      backgroundColor: '#F9FAFB',
      maxHeight: '300px',
      overflowY: 'auto',
      overflowX: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    departmentTableContainer: {
      flex: '0 0 25%',
      minWidth: '100px',
    },
    sectionTableContainer: {
      flex: '0 0 25%',
      minWidth: '100px',
    },
    mappingsTableContainer: {
      flex: '0 0 45%',
      minWidth: '300px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0.75rem',
      fontSize: '0.875rem',
      fontFamily: "'Roboto', sans-serif",
    },
    th: {
      border: '1px solid #ddd',
      padding: '6px',
      background: 'linear-gradient(to right, #F0F0F0, #F3E8FF, #FEE7EF)',
      textAlign: 'center',
      fontSize: '0.75rem',
      fontFamily: "'Roboto', sans-serif",
    },
    td: {
      border: '1px solid #ddd',
      padding: '6px',
      fontSize: '0.75rem',
      fontFamily: "'Roboto', sans-serif",
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      maxWidth: '200px',
    },
    dropdown: {
      padding: '4px 6px',
      minHeight: '25px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '5px',
      cursor: 'pointer',
      fontSize: '0.75rem',
      fontFamily: "'Roboto', sans-serif",
      backgroundColor: '#fff',
      borderColor: selectedSections.length > 0 ? '#d3d3d3' : 'transparent',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      width: '450px',
    },
    selectedDepartmentStyle: {
      border: 'none',
      backgroundColor: '#fff',
      boxShadow: 'none',
      color: '#000000',
    },
    dropdownSearch: {
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: '5px',
      width: '100%',
    },
    searchInput: {
      padding: '8px 40px 8px 12px',
      borderRadius: '5px',
      border: 'none',
      width: '100%',
      fontSize: '0.875rem',
      boxSizing: 'border-box',
      fontFamily: "'Roboto', sans-serif",
      background: 'transparent',
      outline: 'none',
      boxShadow: 'none',
    },
    searchInputDepartment: {
      padding: '8px 40px 8px 12px',
      borderRadius: '5px',
      border: 'none',
      width: '100%',
      fontSize: '0.875rem',
      boxSizing: 'border-box',
      fontFamily: "'Roboto', sans-serif",
      background: 'transparent',
      outline: 'none',
      boxShadow: 'none',
    },
    selectedItem: {
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: '#F0F0F0',
      padding: '2px 5px',
      borderRadius: '3px',
      fontSize: '0.75rem',
      fontFamily: "'Roboto', sans-serif",
      margin: '2px 0',
      border: '1px solid #ccc',
      whiteSpace: 'nowrap',
    },
    removeIcon: {
      marginLeft: '5px',
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#888',
    },
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      width: 'calc(100% - 32px)',
      maxHeight: '150px',
      overflowY: 'auto',
      border: '1px solid #D1D5DB',
      borderRadius: '5px',
      backgroundColor: '#fff',
      zIndex: '1000',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    dropdownItem: {
      padding: '0.5rem',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'background-color 0.3s ease',
      fontSize: '0.875rem',
      color: '#374151',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownItemSelected: {
      backgroundColor: '#e6ecef',
      fontWeight: 'bold',
    },
    actionButtons: { 
      display: 'flex', 
      justifyContent: 'center', 
      gap: '4px',
      fontFamily: "'Roboto', sans-serif",
    },
    editButton: { 
      backgroundColor: 'transparent', 
      color: '#5f77cc', 
      border: 'none', 
      borderRadius: '1px', 
      padding: '1px', 
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
    deleteButton: { 
      backgroundColor: 'transparent', 
      color: '#7a5fcc', 
      border: 'none', 
      borderRadius: '1px', 
      padding: '1px', 
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      fontFamily: "'Roboto', sans-serif",
    },
    modalContent: {
      backgroundColor: '#fff',
      padding: '1rem',
      borderRadius: '8px',
      width: '100%',
      maxWidth: '300px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      minHeight: '100px',
      boxSizing: 'border-box',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    modalHeader: {
      fontSize: '0.875rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    modalInput: {
      width: '100%',
      padding: '0.5rem',
      marginBottom: '0.75rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      transition: 'border-color 0.3s ease',
      boxSizing: 'border-box',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButtons: {
      display: 'flex',
      gap: '0.4rem',
      justifyContent: 'flex-end',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButton: {
      backgroundColor: '#D1D5DB',
      color: '#374151',
      padding: '5px 15px',
      border: 'none',
      borderRadius: '30px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButtonPrimary: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButtonSecondary: {
      backgroundColor: '#D1D5DB',
      color: '#374151',
      border: 'none',
      fontFamily: "'Roboto', sans-serif",
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '0.75rem',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    antSelect: {
      width: '300px',
      padding: '4px 6px',
      borderRadius: '4px',
      minHeight: '25px',
      fontSize: '0.75rem',
      backgroundColor: '#fff',
      boxShadow: 'none',
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #D1D5DB',
    },
    antSelectMultiple: {
      width: '450px',
      minHeight: '25px',
      backgroundColor: '#fff',
      boxShadow: 'none',
      border: '1px solid #D1D5DB',
    },
    antSelectShowArrow: {
      position: 'relative',
    },
    antSelectShowSearch: {
      position: 'relative',
    },
    antSelectSelectionWrap: {
      display: 'flex',
      flexWrap: 'nowrap',
      gap: '3px',
      alignItems: 'center',
      width: '100%',
    },
    antSelectSelectionOverflow: {
      flex: '1 1 auto',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'flex',
      flexWrap: 'nowrap',
    },
    antSelectSelectionWrapModal: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '3px',
      alignItems: 'center',
      width: '100%',
    },
    antSelectSelectionItemRemove: {
      marginLeft: '4px',
      cursor: 'pointer',
      color: '#999',
      fontSize: '10px',
    },
    newDepartmentContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1.1rem',
      backgroundColor: '#F9FAFB',
      borderRadius: '0.375rem',
      fontFamily: "'Roboto', sans-serif",
    },
    newDepartmentInput: {
      flex: '1',
      padding: '0.5rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontFamily: "'Roboto', sans-serif",
    },
    tickButton: {
      backgroundColor: '#6B21A8',
      color: '#fff',
      border: 'none',
      borderRadius: '0.375rem',
      padding: '0.5rem',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    }
  };

  return (
    <div style={layoutStyles.container}>
      <style>
        {`
          .glow-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }

          .glow-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .modal-cancel-button:hover:not(:disabled) {
            background-color: #B0BEC5;
          }

          .input-field:focus {
            border-color: #6B21A8;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }

          .dropdown-item:hover {
            background-color: #f0f0f0;
          }

          .tick-button:hover:not(:disabled) {
            background-color: #5B1A8F;
          }

          @keyframes slideInFromRight {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }

          @keyframes glow {
            0% { box-shadow: 0 0 4px #C5A8FF; }
            50% { box-shadow: 0 0 12px #C5A8FF; }
            100% { box-shadow: 0 0 4px #C5A8FF; }
          }

          .ant-select {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.75rem;
            background-color: #fff;
            box-shadow: none;
          }
          .ant-select-multiple {
            min-height: 25px;
            background-color: #fff;
          }
          .ant-select-outlined .ant-select-selector {
            border: none !important;
            box-shadow: none !important;
          }
          .ant-select-show-arrow::after {
            content: '';
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            border-top: 4px solid #000;
          }
          .ant-select-show-search input {
            padding: 5px;
            border: none;
            outline: none;
            box-shadow: none;
          }
          .ant-select-item-option {
            padding: 5px 12px;
            cursor: pointer;
            position: relative;
          }
          .ant-select-item-option:hover {
            background-color: #f0f0f0;
          }
          .ant-select-selection-wrap {
            display: 'flex';
            flex-wrap: 'nowrap';
            gap: '4px';
            align-items: 'center';
          }
          .ant-select-selection-overflow {
            flex: '1 1 auto';
            min-width: '0';
            overflow: 'hidden';
            text-overflow: 'ellipsis';
            white-space: 'nowrap';
          }
          .ant-select-selection-item-remove {
            margin-left: '4px';
            cursor: 'pointer';
            color: '#999';
            font-size: '10px';
          }
          .ant-select-selection-item-remove:hover {
            color: '#666';
          }
          .ant-select-item-option .remove-icon {
            position: absolute;
            right: 5px;
            top: 50%;
            transform: 'translateY(-50%)';
            cursor: 'pointer';
            color: '#888';
            font-size: '0.75rem';
          }
        `}
      </style>

      <div style={layoutStyles.sidebar}>
        <AdminSidebar />
      </div>
      <div style={layoutStyles.mainContent}>
        <AdminNavbar />
        <div style={layoutStyles.content}>
          <div style={layoutStyles.notificationContainer}>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  ...layoutStyles.notification,
                  ...(notification.type === 'success'
                    ? layoutStyles.notificationSuccess
                    : layoutStyles.notificationError),
                }}
              >
                {notification.message}
                <FontAwesomeIcon
                  icon={faTimes}
                  style={layoutStyles.notificationClose}
                  onClick={() => handleCloseNotification(notification.id)}
                />
              </div>
            ))}
          </div>
          {loading && (
            <div style={layoutStyles.loadingOverlay}>
              <div style={{ fontFamily: "'Roboto', sans-serif" }}>Loading...</div>
            </div>
          )}
          
          <div style={layoutStyles.sectionBox}>
            <div style={layoutStyles.buttonsContainer}>
              <div style={{ position: 'relative' }}>
                <button 
                  style={layoutStyles.button} 
                  className="glow-button" 
                  onClick={() => setIsAddDepartmentModalOpen(true)}
                  disabled={loading}
                >
                  Add Department
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <button 
                  style={layoutStyles.button} 
                  className="glow-button" 
                  onClick={() => setIsAddSectionModalOpen(true)}
                  disabled={loading}
                >
                  Add Section
                </button>
              </div>
            </div>
          </div>

          <div style={layoutStyles.mapSection}>
            <div style={layoutStyles.mapRow}>
              <div style={{ position: 'relative' }}>
                <div 
                  className="ant-select ant-select-outlined css-mc1tut ant-select-show-search ant-select-selection-wrap"
                  ref={departmentDropdownRef}
                  style={{ ...layoutStyles.antSelect, maxWidth: '450px', margin: '0 auto' }}
                >
                  <div className="ant-select-selection-overflow" style={layoutStyles.antSelectSelectionOverflow}>
                    <div className="ant-select-selection-wrap" style={layoutStyles.antSelectSelectionWrap}>
                      <div style={layoutStyles.dropdownSearch}>
                        <input
                          type="text"
                          value={departmentSearch}
                          onChange={(e) => {
                            setDepartmentSearch(e.target.value);
                            if (!isDepartmentDropdownOpen) {
                              setIsDepartmentDropdownOpen(true);
                            }
                          }}
                          onFocus={() => setIsDepartmentDropdownOpen(true)}
                          placeholder={selectedDepartment ? selectedDepartment.name : 'Search departments...'}
                          style={{
                            ...layoutStyles.searchInputDepartment,
                            ...(selectedDepartment ? layoutStyles.selectedDepartmentStyle : {}),
                          }}
                          className="input-field"
                          onKeyDown={handleDepartmentInputKeyDown}
                          disabled={loading}
                          aria-label="Search departments"
                          role="combobox"
                          aria-expanded={isDepartmentDropdownOpen}
                        />
                      </div>
                    </div>
                  </div>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#374151', fontSize: '0.875rem', fontFamily: "'Roboto', sans-serif" }}
                    className="chevron-icon"
                    onClick={() => !loading && setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)}
                    onKeyDown={handleDepartmentIconKeyDown}
                    tabIndex={0}
                    aria-label="Toggle department dropdown"
                    aria-disabled={loading}
                  />
                  {isDepartmentDropdownOpen && (
                    <div className="ant-select-dropdown" style={layoutStyles.dropdownMenu}>
                      {isAddingDepartment && (
                        <div style={layoutStyles.newDepartmentContainer}>
                          <input
                            type="text"
                            value={newDepartment}
                            onChange={(e) => setNewDepartment(e.target.value)}
                            placeholder="Enter new department"
                            style={layoutStyles.newDepartmentInput}
                            className="input-field"
                            disabled={loading}
                          />
                          <button
                            style={layoutStyles.tickButton}
                            onClick={handleAddDepartment}
                            disabled={loading}
                            className="tick-button"
                          >
                            <FontAwesomeIcon
                              icon={faCheck}
                              style={{ border: 'none' }}
                            />
                          </button>
                        </div>
                      )}
                      {filteredDepartments.length === 0 && !isAddingDepartment ? (
                        <div style={{ padding: '5px', color: '#888', fontFamily: "'Roboto', sans-serif" }}>
                          No departments found
                        </div>
                      ) : (
                        filteredDepartments.map((department) => (
                          <div
                            key={department._id}
                            style={layoutStyles.dropdownItem}
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDepartmentSelect(department);
                            }}
                          >
                            <span>{department.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <div 
                  className="ant-select ant-select-outlined css-mc1tut ant-select-multiple ant-select-show-search ant-select-selection-wrap"
                  ref={sectionDropdownRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSectionDropdownOpen(!isSectionDropdownOpen);
                  }}
                  style={{ ...layoutStyles.antSelectMultiple }}
                >
                  <div className="ant-select-selection-overflow" style={layoutStyles.antSelectSelectionOverflow}>
                    <div className="ant-select-selection-wrap" style={layoutStyles.antSelectSelectionWrap}>
                      {selectedSections.map((section) => (
                        <div key={section._id} style={layoutStyles.selectedItem} className="ant-select-selection-item">
                          {section.name}
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={layoutStyles.removeIcon}
                            className="ant-select-selection-item-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSectionSelect(section);
                            }}
                          />
                        </div>
                      ))}
                      <input
                        type="text"
                        value={sectionSearch}
                        onChange={(e) => setSectionSearch(e.target.value)}
                        placeholder="Search sections..."
                        style={layoutStyles.searchInput}
                        className="input-field"
                      />
                    </div>
                  </div>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#374151', fontSize: '0.875rem', fontFamily: "'Roboto', sans-serif" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSectionDropdownOpen(!isSectionDropdownOpen);
                    }}
                  />
                  {isSectionDropdownOpen && (
                    <div className="ant-select-dropdown" style={layoutStyles.dropdownMenu}>
                      {filteredSections.length === 0 ? (
                        <div style={{ padding: '5px', color: '#888', fontFamily: "'Roboto', sans-serif" }}>
                          No sections found
                        </div>
                      ) : (
                        filteredSections.map((section) => {
                          const isSelected = selectedSections.some(s => s._id === section._id);
                          return (
                            <div
                              key={section._id}
                              className="ant-select-item-option"
                              style={isSelected ? layoutStyles.dropdownItemSelected : layoutStyles.dropdownItem}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSectionSelect(section);
                              }}
                            >
                              <span>{section.name}</span>
                              {isSelected && (
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#888', fontSize: '0.75rem' }}
                                  className="remove-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSectionSelect(section);
                                  }}
                                />
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <button 
                  style={{ ...layoutStyles.button, ...layoutStyles.mapButton }} 
                  className="glow-button" 
                  onClick={handleMap}
                  disabled={loading}
                >
                  Map
                </button>
              </div>
            </div>

            <div style={layoutStyles.listContainer}>
              <div style={{ ...layoutStyles.tableContainer, ...layoutStyles.departmentTableContainer }}>
                <table style={layoutStyles.table}>
                  <thead>
                    <tr>
                      <th style={layoutStyles.th}>Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.length === 0 ? (
                      <tr>
                        <td style={layoutStyles.td}>No departments available</td>
                      </tr>
                    ) : (
                      departments.map((department) => (
                        <tr key={department._id}>
                          <td style={layoutStyles.td}>{department.name}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{ ...layoutStyles.tableContainer, ...layoutStyles.sectionTableContainer }}>
                <table style={layoutStyles.table}>
                  <thead>
                    <tr>
                      <th style={layoutStyles.th}>Section</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.length === 0 ? (
                      <tr>
                        <td style={layoutStyles.td}>No sections available</td>
                      </tr>
                    ) : (
                      sections.map((section) => (
                        <tr key={section._id}>
                          <td style={layoutStyles.td}>{section.name}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{ ...layoutStyles.tableContainer, ...layoutStyles.mappingsTableContainer }}>
                <table style={layoutStyles.table}>
                  <thead>
                    <tr>
                      <th style={layoutStyles.th}>Department</th>
                      <th style={layoutStyles.th}>Sections</th>
                      <th style={layoutStyles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedMappings.length === 0 ? (
                      <tr>
                        <td colSpan="3" style={layoutStyles.td}>No mappings available</td>
                      </tr>
                    ) : (
                      groupedMappings.map((group, index) => (
                        <tr key={index}>
                          <td style={layoutStyles.td}>{group.department || 'Unknown Department'}</td>
                          <td style={layoutStyles.td}>{group.sections.join(', ')}</td>
                          <td style={layoutStyles.td}>
                            <div style={layoutStyles.actionButtons}>
                              <button
                                onClick={() => handleEditMapping(group.mappings[0])}
                                style={layoutStyles.editButton}
                                title="Edit"
                                aria-label="Edit mapping"
                                disabled={!group.mappings[0].department}
                              >
                                <EditIcon style={{ fontSize: '14px', fontFamily: "'Roboto', sans-serif" }} />
                              </button>
                              <button
                                onClick={() => handleDeleteDepartment(group.mappings[0]?.department?._id)}
                                style={layoutStyles.deleteButton}
                                title="Delete"
                                aria-label="Delete department and mappings"
                                disabled={!group.mappings[0]?.department?._id}
                              >
                                <DeleteIcon style={{ fontSize: '14px', fontFamily: "'Roboto', sans-serif" }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {isAddSectionModalOpen && (
          <div style={layoutStyles.modalOverlay}>
            <div style={layoutStyles.modalContent}>
              {sectionModalNotification && (
                <div style={layoutStyles.sectionExistsNotificationContainer}>
                  <div
                    style={{
                      ...layoutStyles.modalNotification,
                      ...(sectionModalNotification.type === 'success'
                        ? layoutStyles.notificationSuccess
                        : layoutStyles.notificationError),
                    }}
                  >
                    {sectionModalNotification.message}
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={layoutStyles.notificationClose}
                      onClick={handleCloseSectionModalNotification}
                    />
                  </div>
                </div>
              )}
              <h3 style={layoutStyles.modalHeader}>Add New Section</h3>
              <input
                type="text"
                value={newSection}
                onChange={(e) => setNewSection(e.target.value)}
                placeholder="Enter section name (e.g., F)"
                style={layoutStyles.modalInput}
                className="input-field"
              />
              <div style={layoutStyles.modalButtons}>
                <button
                  style={{ ...layoutStyles.modalButton, ...layoutStyles.modalButtonSecondary }}
                  onClick={() => {
                    setNewSection('');
                    setIsAddSectionModalOpen(false);
                    setSectionModalNotification(null);
                  }}
                  disabled={loading}
                  className="modal-cancel-button"
                >
                  Cancel
                </button>
                <button
                  style={{ ...layoutStyles.modalButton, ...layoutStyles.modalButtonPrimary }}
                  onClick={handleAddSection}
                  disabled={loading}
                  className="glow-button"
                >
                  {loading ? 'Adding...' : 'SAVE'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isAddDepartmentModalOpen && (
          <div style={layoutStyles.modalOverlay}>
            <div style={layoutStyles.modalContent}>
              {departmentModalNotification && (
                <div style={layoutStyles.sectionExistsNotificationContainer}>
                  <div
                    style={{
                      ...layoutStyles.modalNotification,
                      ...(departmentModalNotification.type === 'success'
                        ? layoutStyles.notificationSuccess
                        : layoutStyles.notificationError),
                    }}
                  >
                    {departmentModalNotification.message}
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={layoutStyles.notificationClose}
                      onClick={handleCloseDepartmentModalNotification}
                    />
                  </div>
                </div>
              )}
              <h3 style={layoutStyles.modalHeader}>Add New Department</h3>
              <input
                type="text"
                value={newDepartment}
                onChange={(e) => setNewDepartment(e.target.value)}
                placeholder="Enter department name"
                style={layoutStyles.modalInput}
                className="input-field"
              />
              <div style={layoutStyles.modalButtons}>
                <button
                  style={{ ...layoutStyles.modalButton, ...layoutStyles.modalButtonSecondary }}
                  onClick={() => {
                    setNewDepartment('');
                    setIsAddDepartmentModalOpen(false);
                    setDepartmentModalNotification(null);
                  }}
                  disabled={loading}
                  className="modal-cancel-button"
                >
                  Cancel
                </button>
                <button
                  style={{ ...layoutStyles.modalButton, ...layoutStyles.modalButtonPrimary }}
                  onClick={handleAddDepartment}
                  disabled={loading}
                  className="glow-button"
                >
                  {loading ? 'Adding...' : 'SAVE'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isEditMappingModalOpen && (
          <div style={layoutStyles.modalOverlay}>
            <div style={layoutStyles.modalContent}>
              <h3 style={layoutStyles.modalHeader}>Edit Mapping</h3>
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ ...layoutStyles.modalInput, backgroundColor: '#fff', borderColor: '#D1D5DB' }}>
                  {selectedDepartment ? selectedDepartment.name : 'No department selected'}
                </div>
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                <div 
                  className="ant-select ant-select-outlined css-mc1tut ant-select-multiple ant-select-show-search ant-select-selection-wrap"
                  style={{ ...layoutStyles.antSelectMultiple, maxWidth: '250px' }}
                >
                  <div className="ant-select-selection-overflow" style={layoutStyles.antSelectSelectionOverflow}>
                    <div className="ant-select-selection-wrap" style={layoutStyles.antSelectSelectionWrapModal}>
                      {selectedSections.map((section) => (
                        <div key={section._id} style={layoutStyles.selectedItem} className="ant-select-selection-item">
                          {section.name}
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={layoutStyles.removeIcon}
                            className="ant-select-selection-item-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSectionSelect(section);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div style={layoutStyles.modalButtons}>
                <button
                  style={{ ...layoutStyles.modalButton, ...layoutStyles.modalButtonSecondary }}
                  onClick={() => {
                    setIsEditMappingModalOpen(false);
                    setSelectedMapping(null);
                    setSelectedDepartment(null);
                    setSelectedSections([]);
                  }}
                  disabled={loading}
                  className="modal-cancel-button"
                >
                  Cancel
                </button>
                <button
                  style={{ ...layoutStyles.modalButton, ...layoutStyles.modalButtonPrimary }}
                  onClick={handleSaveMapping}
                  disabled={loading}
                  className="glow-button"
                >
                  {loading ? 'Saving...' : 'SAVE'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddNewSection;